import { Box, CircularProgress } from "@mui/material";
import React, { useState } from "react";
import { useCapture, useCaptureGeneral } from "../../../hooks/Capture";
import { CaptureInfoApiPayload } from "../../../types/forms/capture";
import { CaptureLocationRequest } from "../../../types/model/captureLocation";
import { HydrogeologicContextResponse } from "../../../types/model/hydrogeologicContext";
import CaptureInfo from "../CaptureInfo";

interface GeneralTabProps {
  captureId: string;
  handleError: (error: any) => void;
  canEdit: boolean;
}

const GeneralTab: React.FC<GeneralTabProps> = ({ captureId, handleError, canEdit }) => {
  const [crs, setCrs] = useState<"wgs84" | "etrs89">("etrs89");
  
  const { captureData, updateCapture } = useCapture(captureId);
  const {
    locationData,
    purposesData,
    constructionDetailsData,
    hydrogeologicContextData,
    isPending,
    upsertLocation,
    upsertPurposes,
    upsertConstructionDetails,
    upsertHydrogeologicContext,
    refetchHydrogeologicContext
  } = useCaptureGeneral(captureId, crs);

  if (isPending && !captureData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleCaptureIdentificationUpsert = async (values: CaptureInfoApiPayload) => {
    try {
      const coords = values.captureLocation.coordinates;
      let coordinatesRequest: CaptureLocationRequest["coordinates"];

      if (coords) {
        if ("xETRS" in coords && "yETRS" in coords) {
          coordinatesRequest = {
            crs: "ETRS89",
            xETRS: Number(coords.xETRS),
            yETRS: Number(coords.yETRS),
            zETRS: Number(coords.zETRS),
          };
        } else if ("longitude" in coords && "latitude" in coords) {
          coordinatesRequest = {
            crs: "WGS84",
            longitude: Number(coords.longitude),
            latitude: Number(coords.latitude),
            elevation: Number(coords.elevation),
          };
        }
      }

      const captureLocationRequest: CaptureLocationRequest = {
        mapReference: values.captureLocation.mapReference,
        coordinates: coordinatesRequest,
      };

      const newHydro = values.hydrogeologicContext;

      const oldFlagAffecting = {
        watershedId: hydrogeologicContextData?.watershedId ?? null,
        subWatershedId: hydrogeologicContextData?.subWatershedId ?? null,
        hydrogeologicUnitId: hydrogeologicContextData?.hydrogeologicUnitId ?? null,
        aquiferSystemId: hydrogeologicContextData?.aquiferSystemId ?? null,
        waterBodyId: hydrogeologicContextData?.waterBodyId ?? null,
      };
      
      const newFlagAffecting = {
        watershedId: newHydro.watershedId,
        subWatershedId: newHydro.subWatershedId,
        hydrogeologicUnitId: newHydro.hydrogeologicUnitId,
        aquiferSystemId: newHydro.aquiferSystemId,
        waterBodyId: newHydro.waterBodyId,
      };

      const flagAffectingChange = JSON.stringify(oldFlagAffecting) !== JSON.stringify(newFlagAffecting);

      const promises: Promise<any>[] = [
        updateCapture({
          name: values.capture.name ?? "",
          aquaEpsDesignation: values.capture.aquaEpsDesignation ?? "",
          entityDesignation: values.capture.entityDesignation ?? "",
          licensingDesignation: values.capture.licensingDesignation ?? "",
          otherDesignations: values.capture.otherDesignations ?? {},
          ...(values.capture.licensingDesignationSystem && {
            licensingDesignationSystem: values.capture.licensingDesignationSystem,
          }),
        }),
        upsertPurposes({
          ...purposesData,
          ...values.capturePurposes,
        } as any),
        upsertConstructionDetails({
          ...constructionDetailsData,
          ...values.constructionDetails,
        } as any),
        upsertLocation(captureLocationRequest),
      ];

      if (flagAffectingChange || (JSON.stringify({
        aquiferUnitId: hydrogeologicContextData?.aquiferUnitId ?? null,
        geologyMassId: hydrogeologicContextData?.geologyMassId ?? null,
      }) !== JSON.stringify({
        aquiferUnitId: newHydro.aquiferUnitId,
        geologyMassId: newHydro.geologyMassId,
      }))) {
        const payload = {
          ...newHydro,
          isManuallyInserted: hydrogeologicContextData?.isManuallyInserted || flagAffectingChange,
        };
        promises.push(upsertHydrogeologicContext(payload as HydrogeologicContextResponse));
      }

      await Promise.all(promises);
    } catch (error) {
      handleError(error);
    }
  };

  if (!captureData) return null;

  return (
    <CaptureInfo
      capture={captureData}
      canEdit={canEdit}
      captureLocation={locationData}
      capturePurposes={purposesData}
      constructionDetails={constructionDetailsData}
      hydrogeologicContext={hydrogeologicContextData}
      onSubmit={handleCaptureIdentificationUpsert}
      crs={crs}
      onCrsChange={setCrs}
      onDataRefetch={refetchHydrogeologicContext}
    />
  );
};

export default GeneralTab;
